import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * ScrollToTop - utility component that scrolls the window to the top whenever
 * the route changes. Mount once at the root of the router.
 *
 * Three cases skip the top-scroll:
 *   1. Legacy hash fragments that map to a real route (e.g. `/#contact`) - 
 *      we navigate to the matching page instead of scrolling.
 *   2. Hash fragments that match a section id on the current page - we
 *      scroll that element into view.
 *   3. Accordion sub-routes (`/solutions/<slug>`, `/case-studies/<slug>`) - 
 *      the page itself runs scroll-to-section, so a top-scroll here would
 *      cause an unwanted "top then down" jump.
 */
const ACCORDION_SUBROUTE = /^\/(solutions|case-studies)\/[^/]+$/;

// Legacy hash fragments (from before we switched to BrowserRouter) that
// don't correspond to any on-page section - redirect them to the matching
// route. Keep this list in sync with real route paths.
const LEGACY_HASH_TO_ROUTE = {
  contact: '/contact',
  careers: '/about/careers',
  management: '/about/management',
  glossary: '/resources/glossary',
  connections: '/resources/connections',
};

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '');
      // If there's no element with that id on the current page AND the hash
      // matches a known route, redirect to the route.
      if (LEGACY_HASH_TO_ROUTE[id] && !document.getElementById(id)) {
        navigate(LEGACY_HASH_TO_ROUTE[id], { replace: true });
        return;
      }
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      return;
    }

    if (ACCORDION_SUBROUTE.test(pathname)) {
      // SolutionsIndex / CaseStudiesIndex handle their own scroll-to-section.
      return;
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname, hash, navigate]);

  return null;
};

export default ScrollToTop;
